import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import { toast } from 'sonner'
import SeedTeamEditModal from './SeedTeamEditModal'
import { confirmDialog } from '@/lib/confirmDialog'

const LEVEL_LABELS = {
  boys_mite_8u: 'Boys Mite (8U)',
  boys_squirt_10u: 'Boys Squirt (10U)',
  boys_peewee_12u: 'Boys Peewee (12U)',
  boys_bantam_14u: 'Boys Bantam (14U)',
  girls_8u: 'Girls 8U',
  girls_10u: 'Girls 10U',
  girls_12u: 'Girls 12U',
  girls_15u: 'Girls 15U'
}

function getLevelLabel(value) {
  return LEVEL_LABELS[value] || value || '-'
}

export default function SeedTeamsTable() {
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [editingTeam, setEditingTeam] = useState(null)
  const [syncing, setSyncing] = useState(new Set())

  useEffect(() => {
    fetchTeams()
  }, [])

  async function fetchTeams() {
    try {
      setLoading(true)
      
      let query = supabase
        .from('teams')
        .select('*')
        .eq('data_source', 'seed_auto')
        .order('name')

      const [teamsResponse, pendingResponse] = await Promise.all([
        query,
        supabase
          .from('pending_auto_postings')
          .select('id, team_id')
          .eq('status', 'pending')
      ])

      if (teamsResponse.error) throw teamsResponse.error
      if (pendingResponse.error) throw pendingResponse.error

      const pendingCountMap = new Map()
      ;(pendingResponse.data || []).forEach(row => {
        const current = pendingCountMap.get(row.team_id) || 0
        pendingCountMap.set(row.team_id, current + 1)
      })

      const teamsWithCounts = (teamsResponse.data || []).map(team => ({
        ...team,
        pending_count: pendingCountMap.get(team.id) || 0
      }))

      setTeams(teamsWithCounts)
    } catch (error) {
      console.error('Error fetching seed teams:', error)
      toast.error('Failed to load seed teams')
    } finally {
      setLoading(false)
    }
  }

  async function handleForceSync(team) {
    setSyncing(prev => new Set(prev).add(team.id))
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seed-teams-sync`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            team_ids: [team.id],
            force: true
          })
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Sync failed')
      }

      toast.success(`Synced ${team.name} successfully`)
      fetchTeams() // Refresh to show updated sync status
    } catch (error) {
      console.error('Error syncing team:', error)
      toast.error(`Failed to sync ${team.name}: ${error.message}`)
    } finally {
      setSyncing(prev => {
        const next = new Set(prev)
        next.delete(team.id)
        return next
      })
    }
  }

  async function handleDelete(team) {
    const confirmed = await confirmDialog(`Delete ${team.name}?`, {
      confirmText: 'Delete',
      cancelText: 'Cancel',
      description: 'This removes the seed team along with synced events and pending postings.'
    })

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', team.id)

      if (error) throw error

      toast.success(`Deleted ${team.name}`)
      fetchTeams()
    } catch (error) {
      console.error('Error deleting team:', error)
      toast.error(`Failed to delete team: ${error.message}`)
    }
  }

  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(search.toLowerCase()) ||
                         team.association_name?.toLowerCase().includes(search.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || 
                         team.calendar_sync_status === statusFilter

    return matchesSearch && matchesStatus
  })

  function getStatusBadge(status) {
    switch (status) {
      case 'success':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Success</span>
      case 'failed':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">Failed</span>
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">Pending</span>
      case 'never_synced':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">Never Synced</span>
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">Unknown</span>
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleString()
  }

  if (loading) {
    return (
      <Card>
        <div className="p-8 text-center text-gray-600">Loading seed teams...</div>
      </Card>
    )
  }

  return (
    <>
      <Card>
        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              placeholder="Search teams..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="never_synced">Never Synced</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Association
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pending Opportunities
              </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Sync
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTeams.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {search || statusFilter !== 'all' ? 'No teams match your filters' : 'No seed teams found'}
                  </td>
                </tr>
              ) : (
                filteredTeams.map(team => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{team.name}</div>
                      <div className="text-sm text-gray-500">{team.city}, {team.state}</div>
                      {team.home_rink_name && (
                        <div className="text-xs text-gray-500 mt-1">Primary Rink: {team.home_rink_name}</div>
                      )}
                      {team.home_rink_alt && (
                        <div className="text-xs text-gray-500">Secondary Rink: {team.home_rink_alt}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {team.association_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>{getLevelLabel(team.team_level)}</div>
                      {team.level_detail && (
                        <div className="text-gray-500">Tier {team.level_detail}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {team.pending_count || 0}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(team.last_calendar_sync_succeeded)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(team.calendar_sync_status)}
                      {team.calendar_sync_error && (
                        <div className="text-xs text-red-600 mt-1" title={team.calendar_sync_error}>
                          Error: {team.calendar_sync_error.substring(0, 30)}...
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingTeam(team)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleForceSync(team)}
                          disabled={syncing.has(team.id)}
                        >
                          {syncing.has(team.id) ? 'Syncing...' : 'Sync'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(team)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {filteredTeams.length} of {teams.length} seed teams
          </div>
        </div>
      </Card>

      {editingTeam && (
        <SeedTeamEditModal
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
          onSave={() => {
            setEditingTeam(null)
            fetchTeams()
          }}
        />
      )}
    </>
  )
}

